/**
 * Bande d'ouverture partagee par toutes les pages.
 *
 * Meme fond, memes taches, meme anneau de verre, meme coupure ondulee que
 * l'accueil. C'est ce qui fait qu'un site parait concu plutot qu'assemble :
 * chaque page s'ouvre sur le meme accord.
 *
 * L'accueil garde son propre hero, plus grand, parce qu'il porte le portrait
 * et l'anneau CSS cale sur la tete. Ici l'anneau est le rendu 3D, il devient
 * l'objet principal puisqu'il n'y a pas de visage a encadrer.
 *
 * Les couleurs sont celles prelevees dans la photo de Darius : le fond est
 * celui de son portrait, l'accent celui de son lisere.
 */

import type { ReactNode } from "react";

/** Surface sombre partagee. Sur les themes sombres elle vaut exactement le
 *  fond de la page : plus aucune couture entre les sections. */
export const NUIT = "hsl(var(--nuit))";
export const ACCENT = "hsl(217 95% 52%)";
export const ACCENT_SOURDE = "hsl(217 95% 39%)";

interface Props {
  /** Petite ligne en capitales au-dessus du titre. */
  surtitre?: string;
  titre: ReactNode;
  soustitre?: ReactNode;
  /** Boutons ou liens sous le sous-titre. */
  actions?: ReactNode;
  /** Contenu libre a droite, par exemple une carte de chiffres. */
  aside?: ReactNode;
  /** Bande plus courte pour les pages de contenu. */
  compact?: boolean;
}

export function PageHero({ surtitre, titre, soustitre, actions, aside, compact }: Props) {
  return (
    <section
      className={`relative overflow-hidden ${compact ? "pb-24 pt-28 sm:pb-28" : "pb-32 pt-32 sm:pb-40"}`}
     
    >
      {/* Ni taches ni anneaux ici : FondAnime les porte pour tout le site.
          En garder en plus donnait deux anneaux superposes et une marche de
          luminosite de part et d'autre de la coupure. */}

      <div className="container relative mx-auto px-4 sm:px-6">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            {surtitre && (
              <div className="mb-5 flex items-center gap-2.5">
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: ACCENT }}
                />
                <span
                  className="text-[0.7rem] font-semibold uppercase tracking-[0.24em]"
                  style={{ color: ACCENT }}
                >
                  {surtitre}
                </span>
              </div>
            )}

            <h1 className="text-[clamp(2.1rem,4.6vw,3.3rem)] font-bold leading-[1.05] tracking-[-0.03em] text-white">
              {titre}
            </h1>

            {soustitre && (
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/55 sm:text-lg">
                {soustitre}
              </p>
            )}

            {actions && <div className="mt-8 flex flex-wrap items-center gap-4">{actions}</div>}
          </div>

          {aside && <div className="lg:pb-2">{aside}</div>}
        </div>
      </div>

      {/* La meme coupure ondulee que l'accueil, montante vers la droite. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[70px] sm:h-[110px]"
      >
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 1440 160"
          preserveAspectRatio="none"
          fill="none"
        >
          {/* Deux traits, aucun aplat. Les aplats servaient a masquer un
              changement de couleur de section : il n'y en a plus, et ils
              cachaient le decor de fond, ce qui creait une marche de
              luminosite de part et d'autre de la coupure. */}
          <path
            d="M0,124 C160,90 260,152 420,127 C580,101 660,139 820,103 C980,67 1080,99 1230,65 C1330,43 1390,51 1440,31"
            stroke={ACCENT}
            strokeWidth="5"
            opacity="0.16"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d="M0,118 C160,84 260,146 420,120 C580,94 660,132 820,96 C980,60 1080,92 1230,58 C1330,36 1390,44 1440,24"
            stroke="rgba(255,255,255,0.18)"
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
    </section>
  );
}
